import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Stack,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Snackbar,
  Tooltip,
  IconButton,
  Link,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import api from '../api';
import { useAuth } from '../AuthContext';
import { V2_DESTINATION_COLLECTIONS } from '../constants/v2Collections';
import { getContractKindForCollectionId } from '../constants/v2DestinationContractKinds';

const DEFAULT_BASE_URI_PREFIX = 'https://beta-api.paradisetycoon.com/api/v2/metadata/';

function expectedBaseUriFor(prefix, proxyAddress) {
  if (!prefix || !proxyAddress) return '';
  const cleanPrefix = prefix.endsWith('/') ? prefix : `${prefix}/`;
  return `${cleanPrefix}${String(proxyAddress).toLowerCase()}/`;
}

const KIND_LABELS = {
  preserve: 'ParadiseERC721Preserve',
  reissue: 'ParadiseERC721Reissue',
  erc1155: 'ParadiseERC1155',
};

const NETWORKS = [['avax-cchain', 'Avalanche C-Chain (43114)']];

function explorerAddressUrl(_network, address) {
  if (!address) return null;
  const raw = String(address).trim().startsWith('0x') ? String(address).trim() : `0x${String(address).trim()}`;
  const lower = raw.toLowerCase();
  return `https://snowtrace.io/address/${lower}`;
}

function shortAddr(addr) {
  if (!addr) return '';
  const s = String(addr);
  return s.length <= 14 ? s : `${s.slice(0, 6)}…${s.slice(-4)}`;
}

/** @param {any} st */
function formatEnumerableStatsPreview(st) {
  if (st == null) return '—';
  if (!st.enumerable) return '—';
  if (st.totalSupply == null) return '…';
  if (st.holderScanLimited) {
    return `${st.totalSupply} minted · holders: — (>400)`;
  }
  return `${st.totalSupply} minted · ${st.uniqueHolders} wallets`;
}

/** @param {any} st */
function enumerableStatsTooltip(st, isErc1155) {
  if (isErc1155) return 'ERC-1155';
  if (st == null) return 'Could not read on-chain stats.';
  if (!st.enumerable) {
    return 'Not IERC721Enumerable on this proxy (upgrade + backfill to enable totalSupply / tokenByIndex).';
  }
  if (st.totalSupply == null) return 'totalSupply() failed.';
  if (st.holderScanLimited) {
    return `totalSupply=${st.totalSupply}. Unique wallet count is skipped when supply > 400 (RPC limits).`;
  }
  return `IERC721Enumerable: totalSupply=${st.totalSupply}, distinct ownerOf addresses=${st.uniqueHolders}.`;
}

export default function ContractsManager() {
  const { user } = useAuth();
  const canSnowtraceVerify = user?.role === 'super_admin';

  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedKey, setSelectedKey] = useState('');
  const [relayerOptions, setRelayerOptions] = useState([]);
  const [cchainDeployerEoa, setCchainDeployerEoa] = useState('');
  const [relayerRoles, setRelayerRoles] = useState({});

  const [deployCollectionId, setDeployCollectionId] = useState('paradise-pass-v2');
  const [deployNetwork, setDeployNetwork] = useState('avax-cchain');
  const [upgradeCollectionId, setUpgradeCollectionId] = useState('paradise-pass-v2');
  const [upgradeProxy, setUpgradeProxy] = useState('');
  const [upgradeNetwork, setUpgradeNetwork] = useState('avax-cchain');
  const [grantProxy, setGrantProxy] = useState('');
  const [grantAddr, setGrantAddr] = useState('');
  const [grantNetwork, setGrantNetwork] = useState('avax-cchain');
  const [baseUriPrefix, setBaseUriPrefix] = useState(DEFAULT_BASE_URI_PREFIX);
  const [baseUris, setBaseUris] = useState({});
  const [bulkBaseUriBusy, setBulkBaseUriBusy] = useState(false);
  const [perRowBaseUriBusy, setPerRowBaseUriBusy] = useState({});
  const [collectionDisplays, setCollectionDisplays] = useState({});
  const [perRowBackfillBusy, setPerRowBackfillBusy] = useState({});
  const [perRowSnowtraceBusy, setPerRowSnowtraceBusy] = useState({});
  const [erc721Stats, setErc721Stats] = useState({});

  const [metaNetwork, setMetaNetwork] = useState('avax-cchain');
  const [metaCollectionId, setMetaCollectionId] = useState('paradise-pass-v2');
  const [metaProxy, setMetaProxy] = useState('');
  const [metaName, setMetaName] = useState('');
  const [metaSymbol, setMetaSymbol] = useState('');

  const proxiesOnNetwork = (net) =>
    rows.filter((r) => r.network === net && r.proxyAddress).map((r) => ({
      proxy: String(r.proxyAddress).toLowerCase(),
      collectionId: r.collectionId,
    }));

  const grantProxyChoices = useMemo(() => proxiesOnNetwork(grantNetwork), [rows, grantNetwork]);
  const metaProxyChoices = useMemo(() => proxiesOnNetwork(metaNetwork), [rows, metaNetwork]);

  const deployContractKind = getContractKindForCollectionId(deployCollectionId);
  const upgradeContractKind = getContractKindForCollectionId(upgradeCollectionId);
  const metaContractKind = getContractKindForCollectionId(metaCollectionId);

  useEffect(() => {
    api
      .get('/contracts/relayer-addresses')
      .then((r) => {
        setRelayerOptions(Array.isArray(r.data?.addresses) ? r.data.addresses : []);
        if (typeof r.data?.avaxCchainDeployerEoa === 'string' && r.data.avaxCchainDeployerEoa) {
          setCchainDeployerEoa(r.data.avaxCchainDeployerEoa);
        }
      })
      .catch(() => {
        setRelayerOptions([]);
        setCchainDeployerEoa('');
      });
  }, []);

  const upgradeRowCandidates = useMemo(
    () =>
      rows.filter(
        (r) =>
          r.collectionId === upgradeCollectionId &&
          r.network === upgradeNetwork &&
          r.proxyAddress
      ),
    [rows, upgradeCollectionId, upgradeNetwork]
  );

  useEffect(() => {
    if (upgradeRowCandidates.length === 1) {
      setUpgradeProxy(String(upgradeRowCandidates[0].proxyAddress).toLowerCase());
    } else if (upgradeRowCandidates.length === 0) {
      setUpgradeProxy('');
    } else {
      setUpgradeProxy((prev) => {
        const stillValid =
          prev &&
          upgradeRowCandidates.some((r) => String(r.proxyAddress).toLowerCase() === prev);
        return stillValid ? prev : '';
      });
    }
  }, [upgradeRowCandidates]);

  useEffect(() => {
    setGrantProxy((prev) =>
      prev && rows.some((r) => r.network === grantNetwork && String(r.proxyAddress).toLowerCase() === prev)
        ? prev
        : ''
    );
  }, [rows, grantNetwork]);

  useEffect(() => {
    setMetaProxy((prev) =>
      prev && rows.some((r) => r.network === metaNetwork && String(r.proxyAddress).toLowerCase() === prev)
        ? prev
        : ''
    );
  }, [rows, metaNetwork]);

  useEffect(() => {
    setGrantAddr((prev) => (prev && relayerOptions.includes(prev) ? prev : ''));
  }, [relayerOptions]);

  const refresh = useCallback(async () => {
    setLoading('list');
    setError('');
    try {
      const r = await api.get('/contracts');
      const payload = r.data?.data ?? r.data;
      setRows(Array.isArray(payload) ? payload : []);
    } catch (e) {
      setError(parseErr(e));
    } finally {
      setLoading('');
    }
  }, []);

  const refreshRelayerRoles = useCallback(async () => {
    try {
      const r = await api.get('/contracts/relayer-roles');
      setRelayerRoles(r.data?.roles ?? {});
    } catch (e) {
      console.warn('[ContractsManager] relayer-roles failed:', parseErr(e));
      setRelayerRoles({});
    }
  }, []);

  const refreshBaseUris = useCallback(async () => {
    try {
      const r = await api.get('/contracts/base-uris');
      setBaseUris(r.data?.entries ?? {});
    } catch (e) {
      console.warn('[ContractsManager] base-uris failed:', parseErr(e));
      setBaseUris({});
    }
  }, []);

  const refreshCollectionDisplays = useCallback(async () => {
    try {
      const r = await api.get('/contracts/collection-display');
      setCollectionDisplays(r.data?.entries ?? {});
    } catch (e) {
      console.warn('[ContractsManager] collection-display failed:', parseErr(e));
      setCollectionDisplays({});
    }
  }, []);

  const refreshErc721Stats = useCallback(async () => {
    try {
      const r = await api.get('/contracts/erc721-stats');
      setErc721Stats(r.data?.entries ?? {});
    } catch (e) {
      console.warn('[ContractsManager] erc721-stats failed:', parseErr(e));
      setErc721Stats({});
    }
  }, []);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  useEffect(() => {
    if (rows.length > 0 && relayerOptions.length > 0) {
      refreshRelayerRoles().catch(() => {});
    } else {
      setRelayerRoles({});
    }
  }, [rows, relayerOptions, refreshRelayerRoles]);

  useEffect(() => {
    if (rows.length > 0) {
      refreshBaseUris().catch(() => {});
    } else {
      setBaseUris({});
    }
  }, [rows, refreshBaseUris]);

  useEffect(() => {
    if (rows.length > 0) {
      refreshCollectionDisplays().catch(() => {});
    } else {
      setCollectionDisplays({});
    }
  }, [rows, refreshCollectionDisplays]);

  useEffect(() => {
    if (rows.length > 0) {
      refreshErc721Stats().catch(() => {});
    } else {
      setErc721Stats({});
    }
  }, [rows, refreshErc721Stats]);

  useEffect(() => {
    const c = V2_DESTINATION_COLLECTIONS.find((x) => x.id === metaCollectionId);
    if (c && !metaProxy) {
      setMetaName(c.name);
      setMetaSymbol('');
    }
  }, [metaCollectionId, metaProxy]);

  function parseErr(e) {
    const d = e.response?.data;
    if (d && typeof d === 'object') {
      return d.message || d.error || d.output?.payload?.message || JSON.stringify(d);
    }
    return e.message || String(e);
  }

  const applySelection = (r) => {
    setUpgradeCollectionId(r.collectionId);
    setUpgradeProxy(r.proxyAddress ? String(r.proxyAddress).toLowerCase() : '');
    setUpgradeNetwork(r.network || 'avax-cchain');
    setGrantProxy(r.proxyAddress ? String(r.proxyAddress).toLowerCase() : '');
    setGrantNetwork(r.network || 'avax-cchain');
    setDeployCollectionId(r.collectionId);
    setMetaCollectionId(r.collectionId);
    const proxyLower = r.proxyAddress ? String(r.proxyAddress).toLowerCase() : '';
    setMetaProxy(proxyLower);
    setMetaNetwork(r.network || 'avax-cchain');
    const c = V2_DESTINATION_COLLECTIONS.find((x) => x.id === r.collectionId);
    const disp = proxyLower ? collectionDisplays[proxyLower] : null;
    setMetaName(disp?.name ?? c?.name ?? '');
    setMetaSymbol(disp?.symbol ?? '');
  };

  const post = async (path, body) => {
    setLoading(path);
    setError('');
    setSuccess('');
    try {
      const r = await api.post(`/contracts/${path}`, body);
      const bf = r.data?.backfill;
      if (bf && bf.ok) {
        setSuccess(
          `${path}: ok • backfill ${bf.tokensBackfilled} tokens / ${bf.ownersBackfilled} owners (totalSupply=${bf.onChainTotalSupply}, fromBlock=${bf.fromBlock} via ${bf.fromBlockSource})`
        );
      } else {
        setSuccess(`${path}: ok`);
      }
      await refresh();
      await refreshErc721Stats();
      return r.data;
    } catch (e) {
      setError(parseErr(e));
      throw e;
    } finally {
      setLoading('');
    }
  };

  const backfillRow = useCallback(async (row) => {
    if (!row?.proxyAddress || !row?.collectionId) return;
    const proxyLower = String(row.proxyAddress).toLowerCase();
    setPerRowBackfillBusy((prev) => ({ ...prev, [proxyLower]: true }));
    setError('');
    setSuccess('');
    try {
      const r = await api.post('/contracts/backfill-enumeration', {
        collectionId: row.collectionId,
        targetProxy: proxyLower,
        network: row.network || 'avax-cchain',
      });
      const d = r.data || {};
      setSuccess(
        `backfill ${row.collectionId}: ${d.tokensBackfilled ?? '?'} tokens / ${d.ownersBackfilled ?? '?'} owners (totalSupply=${d.onChainTotalSupply ?? '?'}, fromBlock=${d.fromBlock ?? '?'} via ${d.fromBlockSource ?? '?'})`
      );
      await refreshErc721Stats();
    } catch (e) {
      setError(parseErr(e));
    } finally {
      setPerRowBackfillBusy((prev) => {
        const next = { ...prev };
        delete next[proxyLower];
        return next;
      });
    }
  }, [refreshErc721Stats]);

  const verifySnowtraceRow = useCallback(async (row) => {
    if (!row?.proxyAddress || !row?.collectionId) return;
    const proxyLower = String(row.proxyAddress).toLowerCase();
    setPerRowSnowtraceBusy((prev) => ({ ...prev, [proxyLower]: true }));
    setError('');
    setSuccess('');
    try {
      const r = await api.post('/contracts/snowtrace-verify', {
        collectionId: row.collectionId,
        network: row.network || 'avax-cchain',
        targetProxy: proxyLower,
      });
      const out = typeof r.data?.stdout === 'string' ? r.data.stdout : '';
      const tail = out
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .slice(-5)
        .join(' · ')
        .slice(0, 400);
      setSuccess(`Snowtrace verify OK — ${tail || 'see ptadmin logs for full output'}`);
    } catch (e) {
      setError(parseErr(e));
    } finally {
      setPerRowSnowtraceBusy((prev) => {
        const next = { ...prev };
        delete next[proxyLower];
        return next;
      });
    }
  }, []);

  const copy = async (text) => {
    await navigator.clipboard.writeText(text);
  };

  const summarizeRegistryRows = useCallback(
    () =>
      rows.map((r) => ({
        collectionId: r.collectionId,
        network: r.network,
        contractKind: r.contractKind,
        proxyAddress: r.proxyAddress,
        implementationAddress: r.implementationAddress ?? null,
        version: r.version ?? null,
        deployedAt: r.createdAt ?? null,
      })),
    [rows]
  );

  const copyRegistrySummaryJson = async () => {
    const text = JSON.stringify(summarizeRegistryRows(), null, 2);
    await navigator.clipboard.writeText(text);
    setSuccess('Copied registry summary (JSON, no ABI)');
  };

  const copyRegistrySummaryTsv = async () => {
    const hdr = ['collectionId', 'network', 'contractKind', 'proxy', 'implementation', 'version'].join('\t');
    const lines = summarizeRegistryRows().map((s) =>
      [
        s.collectionId,
        s.network,
        s.contractKind,
        s.proxyAddress,
        s.implementationAddress ?? '',
        s.version ?? '',
      ].join('\t')
    );
    await navigator.clipboard.writeText([hdr, ...lines].join('\n'));
    setSuccess('Copied registry as TSV (paste into Sheets or chat)');
  };

  const setBaseUriForRow = useCallback(
    async (row) => {
      if (!row?.proxyAddress) return;
      const proxyLower = String(row.proxyAddress).toLowerCase();
      const expected = expectedBaseUriFor(baseUriPrefix, proxyLower);
      if (!expected) {
        setError('Set a base URL prefix first');
        return;
      }
      setPerRowBaseUriBusy((prev) => ({ ...prev, [proxyLower]: true }));
      setError('');
      try {
        await api.post('/contracts/set-base-uri', {
          targetProxy: proxyLower,
          baseUri: expected,
          network: row.network || 'avax-cchain',
        });
        setSuccess(`set-base-uri: ${proxyLower} → ${expected}`);
        await refreshBaseUris();
      } catch (e) {
        setError(parseErr(e));
        throw e;
      } finally {
        setPerRowBaseUriBusy((prev) => {
          const next = { ...prev };
          delete next[proxyLower];
          return next;
        });
      }
    },
    [baseUriPrefix, refreshBaseUris]
  );

  const setBaseUriForAll = useCallback(async () => {
    if (!baseUriPrefix) {
      setError('Set a base URL prefix first');
      return;
    }
    const targets = rows.filter((r) => r.proxyAddress && r.network && r.contractKind !== 'erc1155');
    if (targets.length === 0) {
      setError('No deployed ERC-721 proxies in registry');
      return;
    }
    if (
      !window.confirm(
        `Set baseURI on ${targets.length} ERC-721 proxies?\n\nFor each:\n  setBaseURI("${baseUriPrefix}<proxy>/")`
      )
    ) {
      return;
    }
    setBulkBaseUriBusy(true);
    setError('');
    let okCount = 0;
    let failCount = 0;
    try {
      for (const row of targets) {
        try {
          await setBaseUriForRow(row);
          okCount += 1;
        } catch (_) {
          failCount += 1;
        }
      }
      setSuccess(`set-base-uri: ${okCount} ok, ${failCount} failed`);
    } finally {
      setBulkBaseUriBusy(false);
    }
  }, [baseUriPrefix, rows, setBaseUriForRow]);

  const applyCollectionMetadata = useCallback(async () => {
    if (!metaProxy) {
      setError('Select a target proxy');
      return;
    }
    if (!metaContractKind) {
      setError('collectionId has no preserve/reissue mapping in v2DestinationContractKinds.json');
      return;
    }
    if (metaContractKind === 'erc1155') {
      setError('ERC-1155 has no ERC-721 name/symbol');
      return;
    }
    const nameTrim = metaName.trim();
    const symTrim = metaSymbol.trim();
    if (!nameTrim || !symTrim) {
      setError('Set display name and symbol');
      return;
    }
    setLoading('set-collection-metadata');
    setError('');
    setSuccess('');
    try {
      const data = (
        await api.post('/contracts/set-collection-metadata', {
          targetProxy: metaProxy,
          collectionId: metaCollectionId,
          nftName: nameTrim,
          nftSymbol: symTrim,
          network: metaNetwork,
        })
      ).data;
      const h = data?.txHash ? ` (${String(data.txHash).slice(0, 10)}…)` : '';
      setSuccess(`set-collection-metadata: ok${h}`);
      await refreshCollectionDisplays();
      await refresh();
    } catch (e) {
      setError(parseErr(e));
    } finally {
      setLoading('');
    }
  }, [
    metaProxy,
    metaContractKind,
    metaName,
    metaSymbol,
    metaCollectionId,
    metaNetwork,
    refreshCollectionDisplays,
    refresh,
  ]);

  const proxyPlaceholder = '— Pick a deployed proxy (network above) —';
  const relayerPlaceholder =
    relayerOptions.length === 0
      ? '— Configure CONTRACT_RELAYER_ADDRESSES in ptadmin —'
      : '— Select relayer address —';

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        V2 contracts registry
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Click a deployed row to populate upgrade + relayer forms. Deploy / upgrade / Snowtrace verification run Hardhat on
        this ptadmin host (set <code>PARADISE_CONTRACTS_DIR</code>, deploy keys,{' '}
        <code>npx</code>/<code>npm ci</code> under{' '}
        <code>paradise-contracts</code>). Grant uses the same checkout. Allowed relayer addresses come from{' '}
        <code>CONTRACT_RELAYER_ADDRESSES</code>. The game server persists registry rows (<code>/admin/contracts/register-*</code>
        ) using <code>API_KEY</code>; the listing still pulls <code>/admin/contracts</code> from there.
        {cchainDeployerEoa ? (
          <>
            {' '}
            C-Chain deployer EOA (must match <code>AVAX_CCHAIN_DEPLOY_PK</code>):{' '}
            <Box component="span" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
              {cchainDeployerEoa}
            </Box>
            <Tooltip title="Copy address">
              <IconButton size="small" aria-label="copy C-Chain deployer" onClick={() => copy(cchainDeployerEoa)}>
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </>
        ) : null}
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Deploy
            </Typography>
            <Stack spacing={1}>
              <TextField
                label="network"
                size="small"
                fullWidth
                select
                SelectProps={{ native: true }}
                value={deployNetwork}
                onChange={(e) => setDeployNetwork(e.target.value)}
              >
                {NETWORKS.map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </TextField>
              <TextField
                label="collectionId"
                size="small"
                fullWidth
                select
                SelectProps={{ native: true }}
                value={deployCollectionId}
                onChange={(e) => setDeployCollectionId(e.target.value)}
              >
                {V2_DESTINATION_COLLECTIONS.map((c) => {
                  const k = getContractKindForCollectionId(c.id);
                  return (
                    <option key={c.id} value={c.id}>
                      {c.id} — {c.name}
                      {k ? ` (${k})` : ''}
                    </option>
                  );
                })}
              </TextField>
              <Typography variant="body2" color="text.secondary">
                contractKind (from{' '}
                <code>config/v2DestinationContractKinds.json</code>):{' '}
                {deployContractKind ? (
                  <Box component="span" sx={{ fontFamily: 'monospace' }}>
                    {deployContractKind} — {KIND_LABELS[deployContractKind] ?? deployContractKind}
                  </Box>
                ) : (
                  '— add mapping for this collectionId'
                )}
              </Typography>
              <Button
                variant="contained"
                disabled={!!loading || !deployContractKind}
                onClick={() =>
                  post('deploy', {
                    collectionId: deployCollectionId,
                    network: deployNetwork,
                  })
                }
              >
                {loading === 'deploy' ? 'Running…' : 'Deploy'}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Upgrade
            </Typography>
            <Stack spacing={1}>
              <TextField
                label="network"
                size="small"
                fullWidth
                select
                SelectProps={{ native: true }}
                value={upgradeNetwork}
                onChange={(e) => setUpgradeNetwork(e.target.value)}
              >
                {NETWORKS.map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </TextField>
              <TextField
                label="collectionId (registry + cache)"
                size="small"
                fullWidth
                select
                SelectProps={{ native: true }}
                value={upgradeCollectionId}
                onChange={(e) => setUpgradeCollectionId(e.target.value)}
              >
                {V2_DESTINATION_COLLECTIONS.map((c) => {
                  const k = getContractKindForCollectionId(c.id);
                  return (
                    <option key={c.id} value={c.id}>
                      {c.id} — {c.name}
                      {k ? ` (${k})` : ''}
                    </option>
                  );
                })}
              </TextField>
              <TextField
                label="targetProxy (registry)"
                size="small"
                fullWidth
                select
                SelectProps={{ native: true }}
                value={upgradeProxy}
                onChange={(e) => setUpgradeProxy(e.target.value)}
                helperText={
                  upgradeRowCandidates.length === 0
                    ? 'No deployed proxy for this collectionId on this network in the registry.'
                    : upgradeRowCandidates.length === 1
                      ? 'Auto-selected from registry; server resolves proxy if omitted.'
                      : `${upgradeRowCandidates.length} registry rows — choose which proxy to upgrade.`
                }
              >
                {upgradeRowCandidates.length === 0 ? (
                  <option value="">— No proxy in registry —</option>
                ) : null}
                {upgradeRowCandidates.length > 1 ? <option value="">— Choose proxy —</option> : null}
                {upgradeRowCandidates.length > 1
                  ? upgradeRowCandidates.map((r) => (
                      <option key={r.proxyAddress} value={String(r.proxyAddress).toLowerCase()}>
                        {String(r.proxyAddress).toLowerCase()}
                      </option>
                    ))
                  : null}
                {upgradeRowCandidates.length === 1 ? (
                  <option value={String(upgradeRowCandidates[0].proxyAddress).toLowerCase()}>
                    {String(upgradeRowCandidates[0].proxyAddress).toLowerCase()}
                  </option>
                ) : null}
              </TextField>
              <Typography variant="body2" color="text.secondary">
                contractKind (from{' '}
                <code>config/v2DestinationContractKinds.json</code>):{' '}
                {upgradeContractKind ? (
                  <Box component="span" sx={{ fontFamily: 'monospace' }}>
                    {upgradeContractKind} — {KIND_LABELS[upgradeContractKind] ?? upgradeContractKind}
                  </Box>
                ) : (
                  '— add mapping for this collectionId'
                )}
              </Typography>
              <Button
                variant="outlined"
                disabled={
                  !!loading ||
                  !upgradeContractKind ||
                  upgradeRowCandidates.length === 0 ||
                  (upgradeRowCandidates.length > 1 && !upgradeProxy)
                }
                onClick={() =>
                  post('upgrade', {
                    collectionId: upgradeCollectionId,
                    network: upgradeNetwork,
                    ...(upgradeRowCandidates.length > 1 ? { targetProxy: upgradeProxy } : {}),
                  })
                }
              >
                {loading === 'upgrade' ? 'Running…' : 'Upgrade'}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Grant relayer
            </Typography>
            <Stack spacing={1}>
              <TextField
                label="network"
                size="small"
                fullWidth
                select
                SelectProps={{ native: true }}
                value={grantNetwork}
                onChange={(e) => setGrantNetwork(e.target.value)}
              >
                {NETWORKS.map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </TextField>
              <TextField
                label="targetProxy"
                size="small"
                fullWidth
                select
                SelectProps={{ native: true }}
                value={grantProxy}
                onChange={(e) => setGrantProxy(e.target.value)}
              >
                <option value="">{proxyPlaceholder}</option>
                {grantProxyChoices.map((p) => (
                  <option key={`${p.collectionId}-${p.proxy}`} value={p.proxy}>
                    {p.collectionId} — {p.proxy}
                  </option>
                ))}
              </TextField>
              <TextField
                label="relayerAddress"
                size="small"
                fullWidth
                select
                SelectProps={{ native: true }}
                value={grantAddr}
                onChange={(e) => setGrantAddr(e.target.value)}
              >
                <option value="">{relayerPlaceholder}</option>
                {relayerOptions.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </TextField>
              <Button
                variant="outlined"
                disabled={!!loading || !grantProxy || !grantAddr}
                onClick={() =>
                  post('grant-relayer', {
                    targetProxy: grantProxy,
                    relayerAddress: grantAddr,
                    network: grantNetwork,
                  })
                }
              >
                {loading === 'grant-relayer' ? 'Running…' : 'Grant RELAYER_ROLE'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Metadata baseURI
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Sets <code>setBaseURI</code> on each ERC-721 proxy. Final per-contract URI is{' '}
            <code>{'<prefix>'}/{'<proxy-lower>'}/</code>; the contract appends{' '}
            <code>{'<tokenId>'}.json</code>. Match this with the game server route{' '}
            <code>GET /api/v2/metadata/{'{contract}'}/{'{tokenId}'}</code>.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              label="base URL prefix (must end with /)"
              size="small"
              fullWidth
              value={baseUriPrefix}
              onChange={(e) => setBaseUriPrefix(e.target.value)}
              placeholder={DEFAULT_BASE_URI_PREFIX}
            />
            <Button
              variant="contained"
              startIcon={<LinkIcon />}
              disabled={!!loading || bulkBaseUriBusy || rows.length === 0}
              onClick={() => setBaseUriForAll().catch(() => {})}
            >
              {bulkBaseUriBusy ? 'Setting…' : 'Set baseURI on all proxies'}
            </Button>
            <Button variant="text" disabled={rows.length === 0} onClick={() => refreshBaseUris()}>
              Refresh on-chain baseURI
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Collection name &amp; symbol (ERC-721)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Calls <code>setCollectionMetadata(name, symbol)</code> on the proxy (requires upgraded paradise-contracts
            implementation). Uses <code>AVAX_CCHAIN_DEPLOY_PK</code> — deployer must hold{' '}
            <code>DEFAULT_ADMIN_ROLE</code>. The table above reads live <code>name()</code>/<code>symbol()</code> from
            RPC; wallets and marketplaces can lag hours until they re-index.
          </Typography>
          <Stack spacing={1}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <TextField
                label="network"
                size="small"
                select
                SelectProps={{ native: true }}
                value={metaNetwork}
                onChange={(e) => setMetaNetwork(e.target.value)}
              >
                {NETWORKS.map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </TextField>
              <TextField
                label="collectionId"
                size="small"
                fullWidth
                select
                SelectProps={{ native: true }}
                value={metaCollectionId}
                onChange={(e) => setMetaCollectionId(e.target.value)}
              >
                {V2_DESTINATION_COLLECTIONS.map((c) => {
                  const k = getContractKindForCollectionId(c.id);
                  return (
                    <option key={c.id} value={c.id}>
                      {c.id} — {c.name}
                      {k ? ` (${k})` : ''}
                    </option>
                  );
                })}
              </TextField>
              <TextField
                label="targetProxy"
                size="small"
                fullWidth
                select
                SelectProps={{ native: true }}
                value={metaProxy}
                onChange={(e) => {
                  const v = e.target.value;
                  setMetaProxy(v);
                  const d = v ? collectionDisplays[v] : null;
                  const c = V2_DESTINATION_COLLECTIONS.find((x) => x.id === metaCollectionId);
                  if (d) {
                    setMetaName(d.name);
                    setMetaSymbol(d.symbol);
                  } else {
                    setMetaName(c?.name ?? '');
                    setMetaSymbol('');
                  }
                }}
              >
                <option value="">{proxyPlaceholder}</option>
                {metaProxyChoices.map((p) => (
                  <option key={`${p.collectionId}-${p.proxy}`} value={p.proxy}>
                    {p.collectionId} — {p.proxy}
                  </option>
                ))}
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <TextField
                label="ERC-721 name (marketplaces)"
                size="small"
                fullWidth
                value={metaName}
                onChange={(e) => setMetaName(e.target.value)}
              />
              <TextField
                label="symbol"
                size="small"
                fullWidth
                value={metaSymbol}
                onChange={(e) => setMetaSymbol(e.target.value)}
                inputProps={{ maxLength: 32 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              contractKind:{' '}
              {metaContractKind ? (
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                  {metaContractKind}
                </Box>
              ) : (
                '— (add mapping)'
              )}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Button
                variant="contained"
                disabled={
                  !!loading ||
                  !metaProxy ||
                  !metaContractKind ||
                  metaContractKind === 'erc1155' ||
                  !metaName.trim() ||
                  !metaSymbol.trim()
                }
                onClick={() => applyCollectionMetadata().catch(() => {})}
              >
                {loading === 'set-collection-metadata' ? 'Sending tx…' : 'Set name & symbol on-chain'}
              </Button>
              <Button variant="text" disabled={rows.length === 0} onClick={() => refreshCollectionDisplays()}>
                Refresh on-chain name/symbol
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" sx={{ mb: 2 }} flexWrap="wrap" gap={1} alignItems="center">
        <Button
          variant="text"
          onClick={() =>
            Promise.all([
              refresh(),
              refreshRelayerRoles(),
              refreshBaseUris(),
              refreshCollectionDisplays(),
              refreshErc721Stats(),
            ])
          }
          disabled={!!loading}
        >
          Refresh registry
        </Button>
        <Button variant="text" onClick={() => copyRegistrySummaryJson()} disabled={rows.length === 0}>
          Copy summary (JSON)
        </Button>
        <Button variant="text" onClick={() => copyRegistrySummaryTsv()} disabled={rows.length === 0}>
          Copy table (TSV)
        </Button>
      </Stack>

      <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '20%' }}>Collection</TableCell>
              <TableCell sx={{ width: '10%' }}>Proxy</TableCell>
              <TableCell sx={{ width: '10%' }}>Impl</TableCell>
              <TableCell sx={{ width: '12%' }}>RELAYER_ROLE</TableCell>
              <TableCell sx={{ width: '18%' }}>ERC-721</TableCell>
              <TableCell sx={{ width: '14%' }}>Supply / holders</TableCell>
              <TableCell align="center" sx={{ width: '6%' }}>
                baseURI
              </TableCell>
              <TableCell align="right" sx={{ width: '10%' }}>
                {' '}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const proxyLower = r.proxyAddress ? String(r.proxyAddress).toLowerCase() : '';
              const proxyRoles = (proxyLower && relayerRoles[proxyLower]) || {};
              const hasAnyRoleData = Object.keys(proxyRoles).length > 0;
              const baseUriEntry = proxyLower ? baseUris[proxyLower] : null;
              const onChainBaseUri = baseUriEntry?.baseURI ?? null;
              const expectedBaseUri = expectedBaseUriFor(baseUriPrefix, proxyLower);
              const baseMatches = !!onChainBaseUri && !!expectedBaseUri && onChainBaseUri === expectedBaseUri;
              const isErc1155 = r.contractKind === 'erc1155';
              const rowBaseBusy = !!perRowBaseUriBusy[proxyLower];
              const snowBusy = !!perRowSnowtraceBusy[proxyLower];
              const disp = proxyLower ? collectionDisplays[proxyLower] : null;
              const proxyUrl = r.proxyAddress ? explorerAddressUrl(r.network, r.proxyAddress) : null;
              const implUrl = r.implementationAddress ? explorerAddressUrl(r.network, r.implementationAddress) : null;
              const enumStats = proxyLower ? erc721Stats[proxyLower] : null;
              return (
                <TableRow
                  key={`${r.collectionId}-${r.network || 'net'}`}
                  hover
                  selected={selectedKey === r.collectionId}
                  onClick={() => {
                    setSelectedKey(r.collectionId);
                    applySelection(r);
                  }}
                  sx={{ cursor: 'pointer', '& td': { verticalAlign: 'middle' } }}
                >
                  <TableCell sx={{ py: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                      {r.collectionId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                      {r.network} · {r.contractKind}
                      {r.version != null && r.version !== '' ? ` · v${r.version}` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {r.createdAt
                        ? new Date(r.createdAt).toISOString().slice(0, 16).replace('T', ' ')
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell
                    onClick={(e) => e.stopPropagation()}
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {proxyUrl ? (
                      <Link
                        href={proxyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ fontFamily: 'monospace', fontSize: 12 }}
                      >
                        {shortAddr(r.proxyAddress)}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell
                    onClick={(e) => e.stopPropagation()}
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {implUrl ? (
                      <Link
                        href={implUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ fontFamily: 'monospace', fontSize: 12 }}
                      >
                        {shortAddr(r.implementationAddress)}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>
                    {!hasAnyRoleData
                      ? relayerOptions.length === 0
                        ? '—'
                        : '…'
                      : relayerOptions.map((addr) => {
                          const v = proxyRoles[addr];
                          const short = `${addr.slice(0, 6)}…${addr.slice(-4)}`;
                          let glyph = '…';
                          let color;
                          if (v === true) {
                            glyph = '✓';
                            color = 'success.main';
                          } else if (v === false) {
                            glyph = '✗';
                            color = 'error.main';
                          } else if (v === null) {
                            glyph = '?';
                            color = 'warning.main';
                          }
                          return (
                            <Tooltip
                              key={addr}
                              title={`${addr} ${
                                glyph === '✓' ? 'has' : glyph === '✗' ? 'is missing' : 'unknown'
                              } RELAYER_ROLE`}
                            >
                              <Box component="span" sx={{ color, mr: 0.75 }}>
                                {glyph} {short}
                              </Box>
                            </Tooltip>
                          );
                        })}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, maxWidth: 0 }} onClick={(e) => e.stopPropagation()}>
                    {isErc1155 ? (
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        n/a
                      </Box>
                    ) : !disp ? (
                      rows.length > 0 ? '…' : '—'
                    ) : (
                      <Box>
                        <Tooltip title={disp.name}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {disp.name}
                          </Typography>
                        </Tooltip>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                          {disp.symbol || '—'}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: 12, lineHeight: 1.35, wordBreak: 'break-word' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isErc1155 ? (
                      <Box component="span" sx={{ color: 'text.disabled' }}>
                        n/a
                      </Box>
                    ) : (
                      <Tooltip title={enumerableStatsTooltip(enumStats, isErc1155)}>
                        <Box component="span">
                          {rows.length > 0 && proxyLower && !(proxyLower in erc721Stats)
                            ? '…'
                            : formatEnumerableStatsPreview(enumStats)}
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    {isErc1155 ? (
                      <Tooltip title="ERC-1155 — no setBaseURI override on this contract">
                        <Box component="span" sx={{ color: 'text.disabled' }}>
                          —
                        </Box>
                      </Tooltip>
                    ) : !baseUriEntry ? (
                      relayerOptions.length === 0 && rows.length > 0 ? (
                        '—'
                      ) : (
                        '…'
                      )
                    ) : !onChainBaseUri ? (
                      <Tooltip
                        title={
                          baseUriEntry?.sampledTokenId != null && baseUriEntry?.tokenURI === ''
                            ? `Minted token #${baseUriEntry.sampledTokenId} has empty tokenURI (on-chain base not set yet)`
                            : baseUriEntry?.tokenURI || 'Could not read tokenURI for a minted token (try Refresh on-chain baseURI, or ensure IERC721Enumerable + minted supply)'
                        }
                      >
                        <Box component="span" sx={{ color: 'warning.main' }}>
                          ?
                        </Box>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        title={`Open on-chain base URL\n${onChainBaseUri}\n\nExpected: ${expectedBaseUri || '(no prefix)'}\nSampled tokenId: ${baseUriEntry.sampledTokenId ?? '?'}`}
                      >
                        <IconButton
                          component="a"
                          href={onChainBaseUri}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                          sx={{ color: baseMatches ? 'success.main' : 'error.main' }}
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Open baseURI in new tab"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                      <Tooltip title="Copy proxy address">
                        <IconButton size="small" onClick={() => copy(r.proxyAddress)}>
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      {!isErc1155 && proxyLower ? (
                        <Tooltip
                          title={
                            !baseUriPrefix
                              ? 'Set a base URL prefix above first'
                              : baseMatches
                                ? `Already set to ${expectedBaseUri}`
                                : `setBaseURI("${expectedBaseUri}")`
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={!baseUriPrefix || rowBaseBusy || baseMatches || bulkBaseUriBusy}
                              onClick={() => setBaseUriForRow(r).catch(() => {})}
                            >
                              <LinkIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      {!isErc1155 && proxyLower ? (
                        <Tooltip title="Re-run ERC721Enumerable backfill (idempotent)">
                          <span>
                            <IconButton
                              size="small"
                              disabled={!!perRowBackfillBusy[proxyLower]}
                              onClick={() => backfillRow(r).catch(() => {})}
                            >
                              <FormatListNumberedIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      {canSnowtraceVerify && proxyLower ? (
                        <Tooltip
                          title={
                            snowBusy
                              ? 'Snowtrace verification running…'
                              : 'Hardhat verify on Snowtrace (proxy + impl). paradise-contracts checkout must match deployed bytecode.'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={snowBusy}
                              aria-label="Verify on Snowtrace"
                              onClick={() => verifySnowtraceRow(r).catch(() => {})}
                            >
                              <VerifiedOutlinedIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')} message={success} />
    </Box>
  );
}
